/**
 * Colored Logger Utility.
 *
 * Provides colored log output for both:
 * - **Node.js terminal**: ANSI escape codes (\x1b[...m)
 * - **Browser console**: CSS-styled %c formatting
 *
 * Color mapping:
 * - 🔴 Red    (31) → error / danger
 * - 🟢 Green  (32) → success
 * - 🔵 Blue   (34) → info
 * - 🟡 Yellow (33) → warning
 * - 🟣 Purple (35) → data dump / debug context
 * - 🩵 Cyan   (36) → timing / performance
 * - ⚪ Dim    (90) → debug / verbose
 */

const isServer = typeof window === "undefined";
export const isDev = process.env.NODE_ENV !== "production";

// ============================================================
// ANSI Codes (Node.js Terminal)
// ============================================================
const ANSI = {
   reset: "\x1b[0m",
   red: "\x1b[31m",
   green: "\x1b[32m",
   yellow: "\x1b[33m",
   blue: "\x1b[34m",
   purple: "\x1b[35m",
   cyan: "\x1b[36m",
   dim: "\x1b[90m",
   bold: "\x1b[1m",
   bgRed: "\x1b[41m",
   bgGreen: "\x1b[42m",
   bgYellow: "\x1b[43m",
   bgBlue: "\x1b[44m",
   white: "\x1b[37m"
} as const;

// ============================================================
// CSS Styles (Browser Console)
// ============================================================
const CSS = {
   red: "color: #ef4444; font-weight: bold;",
   green: "color: #22c55e; font-weight: bold;",
   yellow: "color: #eab308; font-weight: bold;",
   blue: "color: #3b82f6; font-weight: bold;",
   purple: "color: #a855f7; font-weight: bold;",
   cyan: "color: #06b6d4; font-weight: bold;",
   dim: "color: #9ca3af;",
   reset: "color: inherit; font-weight: normal;"
} as const;

type LogColor = keyof typeof CSS;

// ============================================================
// Core log function (auto-detects environment)
// ============================================================

/** Safely serialize an object arg for terminal output (handles circular refs, BigInt, Error) */
function safeStringify(value: unknown): string {
   if (value instanceof Error) {
      return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ""}`;
   }
   try {
      return JSON.stringify(value, (_key, v) => (typeof v === "bigint" ? `${v}n` : v), 2);
   } catch {
      // Circular reference or other serialization failure
      return String(value);
   }
}

function colorLog(color: LogColor, prefix: string, ...args: unknown[]): void {
   if (isServer) {
      // Node.js: ANSI escape codes
      const ansiColor = ANSI[color] || ANSI.reset;
      const formattedArgs = args.map((arg) =>
         typeof arg === "object" && arg !== null ? safeStringify(arg) : String(arg)
      );
      console.log(`${ansiColor}${prefix}${ANSI.reset}`, ...formattedArgs);
   } else {
      // Browser: CSS-styled %c formatting
      const cssColor = CSS[color] || CSS.reset;
      console.log(`%c${prefix}%c`, cssColor, CSS.reset, ...args);
   }
}

function colorError(prefix: string, ...args: unknown[]): void {
   if (isServer) {
      console.error(`${ANSI.red}${prefix}${ANSI.reset}`, ...args);
   } else {
      console.error(`%c${prefix}%c`, CSS.red, CSS.reset, ...args);
   }
}

function colorWarn(prefix: string, ...args: unknown[]): void {
   if (isServer) {
      console.warn(`${ANSI.yellow}${prefix}${ANSI.reset}`, ...args);
   } else {
      console.warn(`%c${prefix}%c`, CSS.yellow, CSS.reset, ...args);
   }
}

// No-op for production: avoids all serialization overhead
const noop = (): void => {};

// ============================================================
// Public API
// ============================================================

export const logger = {
   /** 🔴 Error / Danger — always active (even in production) */
   error(prefix: string, ...args: unknown[]): void {
      colorError(prefix, ...args);
   },

   /** 🟡 Warning — always active (even in production) */
   warn(prefix: string, ...args: unknown[]): void {
      colorWarn(prefix, ...args);
   },

   /** 🟢 Success — dev only */
   success: isDev
      ? (prefix: string, ...args: unknown[]): void => {
           colorLog("green", prefix, ...args);
        }
      : noop,

   /** 🔵 Info — dev only */
   info: isDev
      ? (prefix: string, ...args: unknown[]): void => {
           colorLog("blue", prefix, ...args);
        }
      : noop,

   /** 🩵 Timing — dev only */
   timing: isDev
      ? (prefix: string, ...args: unknown[]): void => {
           colorLog("cyan", prefix, ...args);
        }
      : noop,

   /** 🟣 Data — dev only */
   data: isDev
      ? (prefix: string, ...args: unknown[]): void => {
           colorLog("purple", prefix, ...args);
        }
      : noop,

   /** ⚪ Debug — dev only */
   debug: isDev
      ? (prefix: string, ...args: unknown[]): void => {
           colorLog("dim", prefix, ...args);
        }
      : noop
} as const;
